import { useContext, useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { request } from "../../../utils/api";
import { Button } from "react-bootstrap";
import { AuthContext } from "../../../auth/AuthContext";
import { toast, ToastContainer } from "react-toastify";
import { showImage } from "../../../utils";
import { FaFileDownload } from "react-icons/fa";
import { AbilityContext } from "../../../context/AbilityContext";
import Pagination from "../utilComponent/Pagination";
import Loading from "../utilComponent/Loading";

const LabelList = () => {
  const navigate = useNavigate();
  const { date } = useParams();
  const { user } = useContext(AuthContext);
  const ability = useContext(AbilityContext);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState({});
  const [totalLabels, setTotalLabels] = useState(0);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
  });
  const [editingId, setEditingId] = useState(null);
  const [editTracking, setEditTracking] = useState("");
  const [editQty, setEditQty] = useState("");
  const [saving, setSaving] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  const isMember = user?.role_key === "member";
  const isAdminOrSuperAdmin = user?.role_key === "super_admin" || "admin";

  const transformData = (data = []) =>
    data.flatMap((item) =>
      item.labels.map((label) => ({
        label_id: label.id,
        label_name: label.label_name,
        label_extract_no: label.label_extract_no,
        product_qty: label.product_qty,
        product_name: label.product?.name,
        product_sku: label.product?.sku,
        warehouse_name: label.product?.warehouse?.name,
        image: label.product?.images[0]?.url,
        date: item.date,
        label_file: label.label_file,
        prep_status: label.prep_status,
        track_status: label.track_status,
      })),
    );

  const getLabelFolder = async (page = 1) => {
    try {
      setLoading(true);

      const res = await request({
        url: `labels-date-wise?page=${page}&per_page=${pagination.per_page}&date=${date}`,
        method: "GET",
      });

      if (res?.status) {
        setProducts(transformData(res.data.items || []));
        setPagination(res.data.pagination);
        setTotal(res.data.totals || {});
        setTotalLabels(res.data.items[0]?.total_labels || 0);
      }
    } catch {
      toast.error("Failed to load labels");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getLabelFolder(pagination.current_page);
  }, [pagination.current_page]);

  const saveEdit = async (item) => {
    try {
      setSaving(true);

      const fd = new FormData();
      fd.append("label_extract_no", editTracking || "");
      fd.append("product_qty", editQty);

      await request({
        url: `labels/${item.label_id}`,
        method: "POST",
        data: fd,
      });

      setProducts((prev) =>
        prev.map((p) =>
          p.label_id === item.label_id
            ? {
                ...p,
                label_extract_no: editTracking,
                product_qty: editQty,
              }
            : p,
        ),
      );

      toast.success("Label updated");
      setEditingId(null);
    } catch {
      toast.error("Update failed");
    } finally {
      setSaving(false);
    }
  };

  const downloadLabel = (labelFile) => {
    const url = labelFile.url;
    window.open(url, "_blank");
    // setTimeout(() => {
    //   const link = document.createElement("a");
    //   link.href = url;
    //   link.download = labelFile.path.split("/").pop();
    //   document.body.appendChild(link);
    //   link.click();
    //   document.body.removeChild(link);
    // }, 500);
  };

  const updateLabelStatus = async (row, status) => {
    if (!status) return;

    try {
      setStatusLoading(true);

      const fd = new FormData();
      fd.append("track_status", status);

      await request({
        url: `labels/${row.label_id}`,
        method: "POST",
        data: fd,
      });

      setProducts((prev) =>
        prev.map((item) =>
          item.label_id === row.label_id ? { ...item, status } : item,
        ),
      );
    } catch {
      toast.error("Status update failed");
    } finally {
      setStatusLoading(false);
    }
  };

  const totals = useMemo(() => {
    return {
      total_label_qty: products.reduce(
        (sum, item) => sum + Number(item.product_qty || 0),
        0,
      ),
      total_product_amount: products.reduce(
        (sum, item) => sum + Number(item.product_qty || 0),
        0,
      ),
    };
  }, [products]);

  return (
    <>
      <ToastContainer />
      <div className="container-fluid">
        <div className="card shadow-sm">
          <div className="card-header d-flex justify-content-between">
            <h6>Label List</h6>
            <Button size="sm" onClick={() => navigate("/label-view")}>
              Back
            </Button>
          </div>
          <div className="row mt-4 g-3 mx-2">
            {[
              ["Total Labels", totalLabels],
              ["Total Product QTY", totals?.total_label_qty],
              ["Total Shipped Label", total?.track_status?.Shipped],
              ["Total Printend Label", total?.prep_status?.Printed],
              ["Total OutofStock Label", total?.prep_status?.["Out of Stock"]],
              ["Total Scanned Label", total?.prep_status?.Scanned],
              ["Total Product Amount", totals?.total_product_amount],
              ["Total Label Amount", totals?.total_product_amount],
            ].map(([label, value]) => (
              <div key={label} className="col-md-3">
                <div className="card text-center shadow-sm">
                  <div className="card-body">
                    <small className="text-muted">{label}</small>
                    <h4 className="fw-bold mt-1">{value}</h4>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="card-body">
            {loading ? (
              <Loading />
            ) : (
              <div className="row">
                {products.map((item) => (
                  <div key={item.label_id} className="col-md-3 mb-4">
                    <div className="card h-100 shadow-sm position-relative">
                      <img
                        src={showImage(item.image)}
                        className="card-img-top"
                        style={{ height: 300, objectFit: "cover" }}
                      />

                      <button
                        onClick={() => downloadLabel(item.label_file)}
                        className="btn btn-sm btn-light position-absolute"
                        style={{ top: 8, right: 8 }}
                      >
                        <FaFileDownload />
                      </button>

                      <div className="card-body">
                        <p>
                          <b>SKU:</b> {item.product_sku}
                        </p>
                        <p>
                          <b>Product:</b> {item.product_name}
                        </p>
                        <p>
                          <p className="mb-1">
                            <b>Prep Status:</b> {item.prep_status}
                          </p>
                          <p className="mb-1">
                            <b>Track Status:</b> {item.prep_status}
                          </p>
                          <b>Tracking No#:</b>{" "}
                          {editingId === item.label_id ? (
                            <input
                              className="form-control form-control-sm"
                              value={editTracking}
                              onChange={(e) => setEditTracking(e.target.value)}
                            />
                          ) : (
                            <span
                              style={{
                                cursor: isMember ? "pointer" : "default",
                                textDecoration: "underline",
                              }}
                              onClick={() => {
                                if (!isMember) return;
                                setEditingId(item.label_id);
                                setEditTracking(item.label_extract_no || "");
                                setEditQty(item.product_qty || "");
                              }}
                            >
                              {item.label_extract_no || "— Click to add —"}
                            </span>
                          )}
                        </p>
                        {isMember && <p>
                          <b>Product Qty:</b>{" "}
                          {item.prep_status === "Label Created" &&
                          ability.can("viewProductQTY", "all") ? (
                            editingId === item.label_id ? (
                              <input
                                type="number"
                                className="form-control form-control-sm"
                                value={editQty}
                                onChange={(e) => setEditQty(e.target.value)}
                              />
                            ) : (
                              <span>{item.product_qty}</span>
                            )
                          ) : (
                            <span className="text-muted">N/A</span>
                          )}
                        </p>}

                        {isAdminOrSuperAdmin && 
                          <p>
                            <b>Product Qty:</b><span className="text-muted">{item.product_qty}</span>
                          </p>
                        }

                        {isMember && (
                          <select
                            className="form-select form-select-sm"
                            disabled={statusLoading}
                            value={item.status}
                            onChange={(e) =>
                              updateLabelStatus(item, e.target.value)
                            }
                          >
                            <option value="">Select Status</option>
                            <option value="Label Created">Label Created</option>
                            <option value="Shipped">Shipped</option>
                            <option value="On the way">On the way</option>
                            <option value="Delivered">Delivered</option>
                            <option value="Out of Stock">Out of Stock</option>
                          </select>
                        )}

                        {editingId === item.label_id && (
                          <div className="d-flex gap-2">
                            <button
                              className="btn btn-success btn-sm"
                              disabled={saving}
                              onClick={() => saveEdit(item)}
                            >
                              Save
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => setEditingId(null)}
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {pagination.last_page > 1 && (
              <Pagination
                currentPage={pagination.current_page}
                lastPage={pagination.last_page}
                onPageChange={(p) =>
                  setPagination((prev) => ({ ...prev, current_page: p }))
                }
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default LabelList;
